import { FC } from 'react';

import { Icon, Link, Ryecon, RyeconConfigure, RyeconLocked } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useMeshProjects } from '@/hooks/discovery/meshProjects';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useProjectScope } from '@/hooks/params';

interface ProjectErrorDisplayProps {
  ryecon: Ryecon;
  header: React.ReactNode;
  message: React.ReactNode;
}

const ProjectErrorDisplay: FC<ProjectErrorDisplayProps> = ({
  ryecon,
  header,
  message,
}) => (
  <div className="mx-auto w-4/12">
    <div className="mt-[30%] text-center">
      <div
        className={`m-auto flex h-[56px] w-[56px] items-center justify-center rounded-md`}
      >
        <Icon size="2xl" ryecon={ryecon} />
      </div>
      <div className="mt-4">
        <h1 className="font-heading-4 mt-2 text-fgAlt">{header}</h1>
        <span className="font-body mt-1 text-fgDecorative">{message}</span>
      </div>
    </div>
  </div>
);

type ProjectErrorParams = {
  accountId: number;
  projectId: number;
};

export const ProjectUnauthorized: FC<ProjectErrorParams> = ({
  accountId,
  projectId,
}) => {
  /**
   * Due to admin API returning an Error if you have no access,
   * we use the MeshProjects endpoint to retrieve the project name
   */
  const [meshProjectsQuery] = useMeshProjects({ accountId, projectId });
  const projectName = meshProjectsQuery.data?.account.meshProjects.find(
    (x) => x.projectId === projectId,
  )?.dbtCloudProject;

  if (meshProjectsQuery.fetching) return null;
  return (
    <ProjectErrorDisplay
      ryecon={RyeconLocked}
      header={`No Access ${projectName ? `to ${projectName}` : ''}`}
      message="Please contact your admin if you'd like permission to view this project."
    />
  );
};

export const ProjectHasNoEnvironments: FC = () => {
  const { projectId } = useDbtCloudProject();
  const projectScope = useProjectScope();

  const getEnvironmentSettingsLink = useLinkToDbtCloud('environmentSettings');

  const productName = 'Catalog';
  return (
    // TODO: This should leverage the EmptyState component
    <ProjectErrorDisplay
      ryecon={RyeconConfigure}
      header="No environments configured"
      message={
        <p>
          Please create and configure a deployment environment in{' '}
          {getEnvironmentSettingsLink ? (
            <Link
              isInternal={false}
              shouldOpenNewTab
              to={getEnvironmentSettingsLink({
                ...projectScope,
                projectId,
              })}
            >
              Environment Settings
            </Link>
          ) : (
            <>dbt Cloud Environment Settings</>
          )}{' '}
          in order to use {productName}. <br /> See{' '}
          <Link
            isInternal={false}
            shouldOpenNewTab
            to={
              'https://docs.getdbt.com/docs/deploy/deploy-environments#set-as-production-environment-beta'
            }
          >
            the docs
          </Link>{' '}
          for further instructions.
        </p>
      }
    />
  );
};
